import { useState } from "react"
import {
    Alert,
    Autocomplete,
    Button,
    Chip,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material"
import { If } from "lib/@components/switch"
import { Repeat } from "lib/@components/repeat"
import { useDemand } from "lib/authorization/use-demand"
import { getGrantableDemandOptions } from "./grantable-demands"
import { BypassControls } from "./bypass-controls"
import { GrantWarning } from "./grant-warning"
import { GrantBundleField } from "./grant-bundle-field"
import { collectGrantWarnings } from "./collect-grant-warnings"
import { getGrantableBundles } from "../controller/get-grantable-bundles"

/**
 * Composing an override (FIQ-1926).
 *
 * Demands and auth bundles are offered together, not as alternatives, because one thing was sold
 * once: a customer who bought a bundle plus one extra capability is a single override, and
 * splitting it into two would make the reason for either invisible to whoever reads the list next
 * year. What the override confers is simply the union of everything named here.
 */
export function GrantEditor({ grant: initial, cancel, ok }) {
    const [grant, setGrant] = useState(() => ({ ...initial }))
    const canForceServerDemands = useDemand("admin")
    const bundles = getGrantableBundles.useResults() ?? []
    const options = getGrantableDemandOptions()
    const selectedBundles = bundles.filter(isSelectedBundle)
    // What the override will ACTUALLY confer. The warnings — the environment gate in particular
    // — have to be worked out from the union: choosing a bundle full of `feature-*` demands would
    // otherwise produce no warning at all, which is precisely when one is most needed.
    const effectiveDemands = [...new Set([...(grant.demands ?? []), ...selectedBundles.flatMap(configuredDemandsOf)])]
    const warnings = collectGrantWarnings(grant, effectiveDemands)
    const problems = validate(grant)

    return (
        <>
            <DialogTitle component="div">
                <Stack spacing={0.25}>
                    <Typography variant="h6">{initial.id ? "Edit override" : "Add override"}</Typography>
                    <Typography variant="caption" color="text.secondary" data-cy="grant-scope-note">
                        {scopeNote()}
                    </Typography>
                </Stack>
            </DialogTitle>
            {/* A capped height so the divider and scrollbar are visible: the Force switches sit
                below the fold on a short window, and an admin who cannot see that there is more
                simply never finds them. */}
            <DialogContent dividers sx={{ maxHeight: "60vh" }}>
                <Stack spacing={1.5} pt={1}>
                    <TextField
                        label="Name"
                        value={grant.name ?? ""}
                        onChange={setName}
                        fullWidth
                        autoFocus
                        required
                        error={!grant.name?.trim()}
                        data-cy="grant-name"
                        helperText="What was sold, so this is recognisable in a year's time"
                    />

                    <Stack spacing={0.25}>
                        <FormControlLabel
                            control={<Switch checked={grant.enabled !== false} onChange={setEnabled} />}
                            label="Enabled"
                            data-cy="grant-enabled"
                        />
                        <Typography variant="caption" color="text.secondary">
                            A disabled override stays listed, with its dates and its note intact, but confers nothing —
                            switch it off rather than deleting it when the reason it exists still matters.
                        </Typography>
                    </Stack>

                    <Autocomplete
                        multiple
                        options={options}
                        groupBy={groupOf}
                        getOptionLabel={labelOf}
                        value={selectedOptions()}
                        onChange={changeDemands}
                        renderInput={renderDemandInput}
                        renderTags={renderDemandTags}
                        data-cy="grant-demands"
                    />

                    <GrantBundleField bundles={bundles} selected={selectedBundles} onSelect={selectBundles} />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                            label="Expires"
                            type="date"
                            value={dateValue(grant.expiresAt)}
                            onChange={setExpires}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            data-cy="grant-expires"
                            helperText="Leave blank for no expiry"
                        />
                        <TextField
                            label="Starts"
                            type="date"
                            value={dateValue(grant.startsAt)}
                            onChange={setStarts}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            data-cy="grant-starts"
                            helperText="Leave blank to start immediately"
                        />
                    </Stack>

                    <TextField
                        label="Justification"
                        value={grant.notes ?? ""}
                        onChange={setNotes}
                        fullWidth
                        multiline
                        required
                        error={!grant.notes?.trim()}
                        rows={2}
                        data-cy="grant-notes"
                        helperText="Why this was granted — ticket reference, who agreed to it"
                    />

                    <BypassControls grant={grant} onChange={setGrant} canForceServerDemands={canForceServerDemands} />

                    <Repeat list={warnings} item={<GrantWarning />} keyFn={keyOfWarning} />

                    <If value={problems.length > 0}>
                        <Alert severity="error" data-cy="grant-problems">
                            <Typography variant="body2">{problems.join(" ")}</Typography>
                        </Alert>
                    </If>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={cancel} color="secondary">
                    Cancel
                </Button>
                <Button variant="contained" disabled={problems.length > 0} onClick={save} data-cy="grant-save">
                    Save
                </Button>
            </DialogActions>
        </>
    )

    function save() {
        ok(payload())
    }

    /**
     * `bundleId` is written alongside `bundleIds` rather than left as it was found. The server
     * reads BOTH — that is what keeps rows written before multi-bundle support working — so a
     * stale legacy id left behind after the admin removed that bundle would keep conferring it.
     */
    function payload() {
        const bundleIds = selectedBundleIds()
        return { ...grant, demands: grant.demands ?? [], bundleIds, bundleId: bundleIds[0] ?? null }
    }

    /**
     * A row written before multi-bundle support carries only `bundleId`, so it is read as one
     * selected bundle and, once saved, becomes an ordinary `bundleIds` row.
     */
    function selectedBundleIds() {
        return [...new Set([...(grant.bundleIds ?? []), grant.bundleId].filter(Boolean))]
    }

    function isSelectedBundle(candidate) {
        return selectedBundleIds().includes(candidate.id)
    }

    function configuredDemandsOf(bundle) {
        return bundle?.demands ?? []
    }

    function selectBundles(ids) {
        setGrant({ ...grant, bundleIds: ids, bundleId: ids[0] ?? null })
    }

    /**
     * Whose capability this is, said out loud.
     *
     * The user Access tab lists the client's overrides beside that user's own, so "this applies
     * to everybody" and "this applies to one person" are otherwise indistinguishable at the
     * moment of editing — which is the moment it matters most.
     */
    function scopeNote() {
        if (grant.scope === "user") return "Applies to this user only"
        return "Applies to the whole client — every user of it, not just this one"
    }

    function setEnabled(e) {
        setGrant({ ...grant, enabled: e.target.checked })
    }

    function setName(e) {
        setGrant({ ...grant, name: e.target.value })
    }

    function setNotes(e) {
        setGrant({ ...grant, notes: e.target.value })
    }

    function setExpires(e) {
        setGrant({ ...grant, expiresAt: toIso(e.target.value, "expiresAt") })
    }

    function setStarts(e) {
        setGrant({ ...grant, startsAt: toIso(e.target.value, "startsAt") })
    }

    function changeDemands(_, selected) {
        setGrant({ ...grant, demands: selected.map(demandOf) })
    }

    function demandOf(option) {
        return option.demand
    }

    function selectedOptions() {
        return options.filter(isSelectedDemand)
    }

    function isSelectedDemand(option) {
        return (grant.demands ?? []).includes(option.demand)
    }

    function groupOf(option) {
        return `From: ${option.group}`
    }

    function labelOf(option) {
        return option.demand
    }

    function renderDemandInput(params) {
        return (
            <TextField
                {...params}
                label="Demands"
                helperText="Grouped by the product that grants them — pick only what was sold"
            />
        )
    }

    function renderDemandTags(value, getTagProps) {
        return value.map(toTag)

        function toTag(option, index) {
            return <Chip {...getTagProps({ index })} key={option.demand} size="small" label={option.demand} />
        }
    }

    function keyOfWarning(warning) {
        return warning.key
    }

    function validate(candidate) {
        const problems = []
        if (!candidate.name?.trim()) problems.push("Give the override a name.")
        if (typeof candidate.notes !== "string" || !candidate.notes.trim())
            problems.push("Add a justification note — who agreed this, and why.")
        // Mirrors the server rule in validate-grant.mjs: an override that confers nothing is one
        // someone will later mistake for a capability that is live.
        if (!(candidate.demands ?? []).length && !selectedBundleIds().length) {
            problems.push("Choose at least one demand or auth bundle.")
        }
        // Also mirrors the server: an indefinite bypass of a safety or delegation gate is the
        // thing that accumulates unnoticed.
        if (hasBypass(candidate) && !candidate.expiresAt) {
            problems.push("An override that forces past a filter must have an expiry date.")
        }
        return problems
    }

    function hasBypass(candidate) {
        return Object.values(candidate.bypass ?? {}).some(Boolean)
    }
}

function dateValue(iso) {
    return iso ? String(iso).slice(0, 10) : ""
}

function toIso(value, field) {
    if (!value) return null
    // Expiry runs to the end of the chosen day; a start date begins at the beginning of it.
    return field === "expiresAt" ? `${value}T23:59:59.999Z` : `${value}T00:00:00.000Z`
}
